import React, { useState } from 'react';
import config, { authFetch } from '../config';
import './PDFUploader.css';

const PDFUploader = ({ onPDFLoad, reviewId, onCancel }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [csvFile, setCsvFile] = useState(null);

  const handlePDFChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Please upload a PDF file');
      return;
    }

    setPdfFile(file);
    setError(null);
  };

  const handleCSVChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    setCsvFile(file);
    setError(null);
  };

  const handleUpload = async () => {
    if (!pdfFile) {
      setError('Please select a PDF file');
      return;
    }

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('pdf', pdfFile);
    if (csvFile) {
      formData.append('csv', csvFile);
    }

    try {
      // Ensure no double slashes
      const apiUrl = config.API_URL.endsWith('/') ? config.API_URL.slice(0, -1) : config.API_URL;
      const uploadUrl = reviewId 
        ? `${apiUrl}/api/reviews/${reviewId}/pdfs`
        : `${apiUrl}/api/upload`;
      const response = await authFetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(errorData.error || 'Upload failed');
      }

      const data = await response.json();
      // Ensure no double slashes in URL
      const baseUrl = config.API_URL.endsWith('/') ? config.API_URL.slice(0, -1) : config.API_URL;
      const pdfPath = data.pdfUrl.startsWith('/') ? data.pdfUrl : `/${data.pdfUrl}`;
      const fullUrl = `${baseUrl}${pdfPath}`;
      onPDFLoad(fullUrl, data.pdfId, data.shareId);
      
      // Reset files
      setPdfFile(null);
      setCsvFile(null);
    } catch (err) {
      let errorMessage = err.message || 'Failed to upload PDF. Please try again.';
      // Connection refused / server not running
      if (
        err.message?.includes('Failed to fetch') ||
        err.message?.includes('ERR_CONNECTION_REFUSED') ||
        err.message?.includes('NetworkError') ||
        (err.name === 'TypeError' && err.message === 'Failed to fetch')
      ) {
        errorMessage = `Cannot connect to server at ${config.API_URL}. Start the backend with: cd server && npm run dev (or run "npm run dev" from the project root).`;
      }
      setError(errorMessage);
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePDF = () => {
    setPdfFile(null);
  };

  const handleRemoveCSV = () => {
    setCsvFile(null);
  };

  return (
    <div className="uploader-container">
      <div className="uploader-box">
        
        <h2>Upload a PDF to get started</h2>
        <p>Select a PDF file to view and add comments collaboratively</p>
        
        <div className="upload-files">
          <div className="file-upload-section">
            <label className="upload-button">
              {pdfFile ? 'Change PDF File' : 'Choose PDF File *'}
              <input
                type="file"
                accept="application/pdf"
                onChange={handlePDFChange}
                disabled={uploading}
                style={{ display: 'none' }}
              />
            </label>
            {pdfFile && (
              <div className="file-info">
                <span className="file-name">📄 {pdfFile.name}</span>
                <button className="remove-file-btn" onClick={handleRemovePDF} disabled={uploading}>
                  ✕
                </button>
              </div>
            )}
          </div>

          <div className="file-upload-section">
            <label className="upload-button csv-button">
              {csvFile ? 'Change CSV File' : 'Choose CSV File (Optional)'}
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={handleCSVChange}
                disabled={uploading}
                style={{ display: 'none' }}
              />
            </label>
            {csvFile && (
              <div className="file-info">
                <span className="file-name">📊 {csvFile.name}</span>
                <button className="remove-file-btn" onClick={handleRemoveCSV} disabled={uploading}>
                  ✕
                </button>
              </div>
            )}
            <p className="csv-hint">CSV format: Page Number, X, Y, Comment Text, Author (optional), Timestamp (optional)</p>
          </div>
        </div>

        <div className="upload-actions">
          <button 
            className="upload-submit-button" 
            onClick={handleUpload}
            disabled={uploading || !pdfFile}
          >
            {uploading ? 'Uploading...' : 'Upload PDF'}
          </button>
          {onCancel && (
            <button 
              className="upload-cancel-button" 
              onClick={onCancel}
              disabled={uploading}
            >
              Cancel
            </button>
          )}
        </div>
        
        {error && <div className="error-message">{error}</div>}
      </div>
    </div>
  );
};

export default PDFUploader;



